import { useCallback, useEffect, useMemo, useState } from "react"
import { get, post, put } from "../../api/api"

const LABELS = {
  general: "General",
  api: "API",
  openai: "OpenAI",
  crawl: "Crawler",
  seo: "SEO Defaults",
  features: "Features",
  serp: "SERP",
  smtp: "Email / SMTP",
  billing: "Billing"
}

const DESCRIPTIONS = {
  general: "Brand, public URL, support email and signup behavior.",
  api: "Public API access, rate limits, webhooks and signing secrets.",
  openai: "AI model and encrypted key used for recommendations and content.",
  crawl: "Crawler safety, speed, depth and request behavior.",
  seo: "Report scoring defaults, retention and roadmap behavior.",
  features: "Enable or hide product modules for user workspaces.",
  serp: "Live search provider for rank tracking, competitors and keyword gaps.",
  smtp: "Transactional email server settings.",
  billing: "Currency, trials, payment policy and provider keys."
}

function inputType(type) {
  if (type === "secret") return "password"
  if (type === "boolean") return "checkbox"
  if (type === "number") return "number"
  return type || "text"
}

export default function Settings() {
  const [grouped, setGrouped] = useState({})
  const [values, setValues] = useState({})
  const [active, setActive] = useState("general")
  const [system, setSystem] = useState(null)
  const [status, setStatus] = useState("")
  const [loading, setLoading] = useState(true)

  const categories = useMemo(() => Object.keys(grouped), [grouped])

  const hydrate = useCallback((payload) => {
    const nextGrouped = payload.grouped || {}
    const nextValues = {}

    Object.values(nextGrouped)
      .flat()
      .forEach((field) => {
        nextValues[field.key] = field.type === "boolean" ? Boolean(field.value) : field.value || ""
      })

    setGrouped(nextGrouped)
    setValues(nextValues)
    setActive((current) => (nextGrouped[current] ? current : Object.keys(nextGrouped)[0] || "general"))
  }, [])

  useEffect(() => {
    async function loadSettings() {
      const [settingsRes, systemRes] = await Promise.all([
        get("/superadmin/settings"),
        get("/superadmin/system")
      ])

      hydrate(settingsRes.data)
      setSystem(systemRes.data)
    }

    loadSettings()
      .catch((err) => setStatus(err.response?.data?.error || "Failed to load settings"))
      .finally(() => setLoading(false))
  }, [hydrate])

  function update(key, value) {
    setValues((current) => ({ ...current, [key]: value }))
  }

  async function save(e) {
    e.preventDefault()
    setStatus("Saving...")

    try {
      const res = await put("/superadmin/settings", values)
      hydrate(res.data)
      setStatus("Settings saved.")
    } catch (err) {
      setStatus(err.response?.data?.error || "Save failed")
    }
  }

  async function testOpenAI() {
    setStatus("Testing OpenAI...")

    try {
      const res = await post("/superadmin/settings/test/openai", {})
      setStatus(res.data.message)
    } catch (err) {
      setStatus(err.response?.data?.error || "OpenAI test failed")
    }
  }

  if (loading) return <div className="page">Loading settings...</div>

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>App & API Settings</h1>
          <p className="muted">
            Control the product, billing, API keys, crawler limits, feature access and provider integrations.
          </p>
        </div>
        <button onClick={testOpenAI}>Test OpenAI</button>
      </div>

      {system && (
        <div className="grid">
          <div className="card">
            <p className="muted">MongoDB</p>
            <div className={`status ${system.mongo === "connected" ? "active" : "failed"}`}>
              {system.mongo}
            </div>
          </div>
          <div className="card">
            <p className="muted">Redis configured</p>
            <div className={`status ${system.redisConfigured ? "active" : "inactive"}`}>
              {system.redisConfigured ? "Yes" : "No"}
            </div>
          </div>
          <div className="card">
            <p className="muted">JWT secret</p>
            <div className={`status ${system.secrets?.jwtSecret ? "active" : "failed"}`}>
              {system.secrets?.jwtSecret ? "Set" : "Missing"}
            </div>
          </div>
        </div>
      )}

      <div className="settings-shell">
        <div className="settings-nav card">
          <h2>Control Areas</h2>
          {categories.map((category) => (
            <button
              type="button"
              key={category}
              className={active === category ? "active" : ""}
              onClick={() => setActive(category)}
            >
              <strong>{LABELS[category] || category}</strong>
              <span>{DESCRIPTIONS[category] || "Platform settings"}</span>
            </button>
          ))}
        </div>

        <form className="card settings-panel" onSubmit={save}>
          <div className="settings-panel-head">
            <div>
              <p className="eyebrow">Super Admin</p>
              <h2>{LABELS[active] || active}</h2>
              <p className="muted">{DESCRIPTIONS[active] || "Manage this control area."}</p>
            </div>
            <button type="submit">Save settings</button>
          </div>

          <div className="settings-grid">
            {(grouped[active] || []).map((field) => (
              <label className={field.type === "boolean" ? "setting-toggle" : ""} key={field.key}>
                <span>
                  <strong>{field.label}</strong>
                  {field.description && <small>{field.description}</small>}
                </span>
                {field.type === "select" ? (
                  <select
                    value={values[field.key] || field.defaultValue || ""}
                    onChange={(e) => update(field.key, e.target.value)}
                  >
                    {(field.options || []).map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                ) : field.type === "boolean" ? (
                  <input
                    type="checkbox"
                    checked={Boolean(values[field.key])}
                    onChange={(e) => update(field.key, e.target.checked)}
                  />
                ) : (
                  <input
                    type={inputType(field.type)}
                    value={values[field.key] || ""}
                    placeholder={field.hasValue ? "Saved value is hidden" : ""}
                    onChange={(e) => update(field.key, e.target.value)}
                  />
                )}
                {field.secret && field.hasValue && (
                  <span className="muted">Existing secret is saved and masked.</span>
                )}
              </label>
            ))}
          </div>

          {status && <p className="muted">{status}</p>}
        </form>
      </div>
    </div>
  )
}
